import { commentTable, reactionTable, readSyncMeta, writeSyncMeta } from '../db/entityCache';
import { Hazard, Comment, Reaction } from '../db/types';
import eventEmitter, { EVENTS } from './eventService';
import reactionService from './reactionService';
import eventStreamService from './eventStreamService';
import moment from 'moment';
import API_BASE_URL from '../config';
import logger, { LogCategory } from '../utils/logger';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { isTenantDisabledNow } from '../utils/tenantStatus';
import userProfileService from './userProfileService';
import { uploadFileViaSas } from './mediaUploadService';
import { buildSubmitPayload, dataUrlToFile, mapWithConcurrency, normalizeMediaRefsForSync, assertStorageForFiles } from './syncUtils';
import * as hazardGateway from '../db/hazardGateway';
import { newerResolvedThan, noteHazardResolved } from './hazardResolvedStamp';
import {
  CommunityFilters,
  CommunityFilterFacets,
  SearchField,
  SeverityLevel,
  SEVERITY_ORDER,
} from '../types/communityFilters';

// Helper function to get current user ID from OIDC tokens
const getCurrentUserId = (): string | null => {
  try {
    const userInfo = localStorage.getItem('oidc_user_info');
    if (userInfo) {
      const parsed = JSON.parse(userInfo);
      return parsed.userId || null;
    }
    return null;
  } catch (error) {
    logger.authError('Error getting current user ID from OIDC tokens', error);
    return null;
  }
};

// Safe date parsing function for sorting - handles iOS Safari date parsing issues
const safeDateParse = (dateString: string): number => {
  try {
    // Detect iOS Safari specifically
    const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) && 
                       /Safari/.test(navigator.userAgent) && 
                       !/Chrome|CriOS|FxiOS/.test(navigator.userAgent);
    
    let date: Date;
    
    if (isIOSSafari) {
      // iOS Safari specific date parsing with timezone preservation
      let processedDate = dateString.trim();
      
      // Remove milliseconds if present (iOS Safari doesn't handle them well)
      processedDate = processedDate.replace(/\.\d{1,3}/, '');
      
      // Handle both ISO format (with T) and SQL Server format (with space)
      // Regex to match both: YYYY-MM-DD[T ]HH:MM:SS[timezone]
      const dateMatch = processedDate.match(/(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}):(\d{2})(.*))?$/);
      
      if (dateMatch) {
        const [, year, month, day, hour = '0', minute = '0', second = '0', tzPart = ''] = dateMatch;
        
        // Check if timezone info is present
        const hasTimezoneInfo = tzPart && (tzPart.includes('Z') || tzPart.includes('+') || tzPart.includes('-'));
        
        if (hasTimezoneInfo) {
          // Handle timezone offset
          if (tzPart === 'Z' || tzPart.trim() === '') {
            // UTC time
            date = new Date(Date.UTC(
              parseInt(year),
              parseInt(month) - 1,
              parseInt(day),
              parseInt(hour),
              parseInt(minute),
              parseInt(second)
            ));
          } else {
            // Handle timezone offset
            const offsetMatch = tzPart.match(/([+-])(\d{2}):?(\d{2})$/);
            if (offsetMatch) {
              const [, sign, offsetHours, offsetMinutes] = offsetMatch;
              const offsetMs = (parseInt(offsetHours) * 60 + parseInt(offsetMinutes)) * 60 * 1000;
              
              // Create UTC time first, then adjust for offset
              const utcTime = Date.UTC(
                parseInt(year),
                parseInt(month) - 1,
                parseInt(day),
                parseInt(hour),
                parseInt(minute),
                parseInt(second)
              );
              
              // Apply timezone offset to get actual UTC time
              date = new Date(utcTime - (sign === '+' ? offsetMs : -offsetMs));
            } else {
              // Fallback: treat as UTC
              date = new Date(Date.UTC(
                parseInt(year),
                parseInt(month) - 1,
                parseInt(day),
                parseInt(hour),
                parseInt(minute),
                parseInt(second)
              ));
            }
          }
        } else {
          // No timezone info - assume it's already UTC (SQL Server datetime)
          // Use Date.UTC to ensure we get the exact time specified
          date = new Date(Date.UTC(
            parseInt(year),
            parseInt(month) - 1,
            parseInt(day),
            parseInt(hour),
            parseInt(minute),
            parseInt(second)
          ));
        }
      } else {
        // Fallback: try to create a Safari-friendly format
        // Convert space to T if needed, then parse
        const safariFormat = processedDate.includes('T') 
          ? processedDate 
          : processedDate.replace(' ', 'T') + 'Z';
        date = new Date(safariFormat);
      }
    } else {
      // Non-iOS Safari browsers - use existing logic but handle SQL Server format
      date = new Date(dateString);
      
      // Check if the date is invalid (Safari often returns Invalid Date)
      if (isNaN(date.getTime())) {
        // Try converting SQL Server format to ISO format
        let safariCompatibleDate = dateString;
        
        // If it looks like SQL Server format (YYYY-MM-DD HH:MM:SS), convert to ISO
        if (safariCompatibleDate.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/)) {
          safariCompatibleDate = safariCompatibleDate.replace(' ', 'T') + 'Z';
        }
        // If it's an ISO string without timezone, add Z for UTC
        else if (dateString.includes('T') && !dateString.includes('Z') && !dateString.includes('+')) {
          safariCompatibleDate = dateString + 'Z';
        }
        
        // Try parsing again with Safari-compatible format
        date = new Date(safariCompatibleDate);
        
        // If still invalid, try with space instead of T
        if (isNaN(date.getTime())) {
          safariCompatibleDate = dateString.replace('T', ' ').replace('Z', '');
          date = new Date(safariCompatibleDate);
        }
      }
    }
    
    // Return timestamp or fallback value for invalid dates
    const timestamp = date.getTime();
    if (isNaN(timestamp)) {
      logger.hazardsWarn('Failed to parse date for sorting', { dateString });
      // Return a very old timestamp so invalid dates sort to the bottom
      return 0;
    }
    
    return timestamp;
  } catch (error) {
    logger.hazardsWarn('Error parsing date for sorting', { dateString, error });
    // Return a very old timestamp so invalid dates sort to the bottom
    return 0;
  }
};

/** Newest utc_timestamp first, then id DESC so equal timestamps match the server. */
const compareHazardsNewestFirst = (
  a: { utc_timestamp: string; id: string },
  b: { utc_timestamp: string; id: string }
): number => {
  const byTime = safeDateParse(b.utc_timestamp) - safeDateParse(a.utc_timestamp);
  if (byTime !== 0) return byTime;
  return b.id.localeCompare(a.id);
};

/**
 * Record a failed push attempt on a hazard row: bump `sync_attempts`, optionally
 * stamp `sync_error`, and mark the row `sync_dead` when the failure is terminal
 * (INVALID_MEDIA_REFS / MISSING_LOCATION / HTTP 400 / 413 / 409) or the attempt
 * budget is exhausted. Retryable failures (network errors, 5xx, 429) only
 * increment the counter.
 */
async function recordFailedHazardAttempt(
  hazardId: string,
  options: { syncError?: string; terminal?: boolean; pause?: boolean; changes?: Partial<Hazard> } = {}
): Promise<void> {
  const { syncError, terminal = false, pause = false } = options;
  try {
    await hazardGateway.recordFailure(hazardId, { syncError, terminal, pause });
  } catch (error) {
    console.warn(`Could not record failed sync attempt for hazard ${hazardId}:`, error);
  }
}

// Helper function to push a single unsynced submission from the main hazards table
async function pushSingleHazardSubmission(hazard: Hazard): Promise<boolean> {
  // Check if hazard is valid
  if (!hazard) {
    return true;
  }

  // For deleted hazards, we need to sync them even if they have a last_synced value
  // because the deletion might not have been synced yet
  if (!hazard.deleted && hazard.last_synced) {
    return true;
  }

  if (!navigator.onLine) {
    return false;
  }

  // IMPORTANT: Check if this is truly a new hazard or an updated existing hazard
  // New hazards should be submitted to /api/handleSubmit
  // Updated hazards should be synced via the updateHazard sync mechanism

  // Do not treat presence in communityHazards as server-synced.
  // We always attempt submission for items without last_synced, regardless of local community cache.

  // STEP 1: Prepare sync time but only mark as synced after successful API call
  const syncTime = moment().format();
  // For new hazards, we'll mark as synced after getting blob URLs from server

  const imagesForSync = normalizeMediaRefsForSync(hazard.image_data);
  if (!hazard.deleted && (hazard.image_data?.length ?? 0) > 0 && imagesForSync.length === 0) {
    // Terminal: the stored entries can never produce a valid server payload.
    await recordFailedHazardAttempt(hazard.id, { syncError: 'INVALID_MEDIA_REFS', terminal: true });
    console.warn(`Skipping hazard ${hazard.id} sync - no valid media references for server payload`);
    return false;
  }

  // Defense-in-depth: never sync a hazard without a real location. Coerce
  // defensively because anomalous cached rows may hold null/undefined/NaN
  // despite the typed number fields, and 0,0 is treated as "no location".
  if (!hazard.deleted) {
    const rawLat = hazard.latitude as unknown;
    const rawLng = hazard.longitude as unknown;
    const lat = rawLat === null || rawLat === undefined || rawLat === '' ? NaN : Number(rawLat);
    const lng = rawLng === null || rawLng === undefined || rawLng === '' ? NaN : Number(rawLng);
    const hasValidLocation =
      Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0);
    if (!hasValidLocation) {
      // Terminal: a hazard without real coordinates can never be submitted.
      await recordFailedHazardAttempt(hazard.id, { syncError: 'MISSING_LOCATION', terminal: true });
      console.warn(`Skipping hazard ${hazard.id} sync - missing/zero coordinates`);
      return false;
    }
  }

  // STEP 1.5: Media at push. Upload any pending data: refs to blob storage so
  // the POST body only carries http(s) URLs. Uploaded refs are persisted back
  // to the Dexie row BEFORE the POST so retries never re-upload; refs that
  // fail to upload stay as data: for the next attempt.
  let mediaRefsForPayload = imagesForSync;
  if (!hazard.deleted && imagesForSync.some((ref) => ref.startsWith('data:'))) {
    let anyUploaded = false;
    mediaRefsForPayload = await mapWithConcurrency(imagesForSync, 3, async (ref, index) => {
      if (!ref.startsWith('data:')) {
        return ref;
      }
      try {
        const file = dataUrlToFile(ref, `hazard-media-${index}`);
        const { blobUrl } = await uploadFileViaSas({ file });
        anyUploaded = true;
        return blobUrl;
      } catch (error) {
        console.warn(`Failed to upload pending media ref for hazard ${hazard.id}; keeping data URL for next attempt:`, error);
        return ref;
      }
    });
    if (anyUploaded) {
      try {
        await hazardGateway.update(hazard.id, { image_data: mediaRefsForPayload });
      } catch (updateError) {
        console.warn(`Could not persist uploaded media refs for hazard ${hazard.id}:`, updateError);
      }
    }
  }

  // STEP 2: Prepare payload and make API call. The payload is built by the
  // pure builder: the `tags` key is only sent when the tags came from the
  // server classifier, so local/lexicon tags never suppress server AI tagging.
  // The deleted-hazard path (`{ id }` to softDeleteHazard) stays unchanged.
  const payload = hazard.deleted
    ? { id: hazard.id }
    : buildSubmitPayload({ ...hazard, image_data: mediaRefsForPayload });

  // STEP 3: Make API call and wait for completion
  try {
    const endpoint = hazard.deleted ? `${API_BASE_URL}/api/softDeleteHazard` : `${API_BASE_URL}/api/handleSubmit`;

    const response = await fetchWithAuth(endpoint, {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    if (response.ok) {
      // Update communityHazards table
      if (hazard.deleted) {
        try {
          await hazardGateway.update(hazard.id, {
            deleted: true,
            last_synced: syncTime
          });
        } catch (updateError) {
          console.warn(`[DELETION] ⚠️ Could not update communityHazards for deleted hazard ${hazard.id}:`, updateError);
        }
      } else {
        try {
          const serverHazardData = await response.json();
          const serverTags = Array.isArray(serverHazardData?.tags)
            ? serverHazardData.tags.filter((tag: unknown): tag is string => typeof tag === 'string')
            : [];
          const customTags = Array.isArray((hazard as Hazard & { custom_tags?: unknown }).custom_tags)
            ? (hazard as Hazard & { custom_tags: unknown[] }).custom_tags.filter((tag): tag is string => typeof tag === 'string')
            : [];
          const imagePatch = serverHazardData?.image_data
            ? {
                image_data: serverHazardData.image_data,
                image_count: serverHazardData.image_count || serverHazardData.image_data.length,
                media: serverHazardData.media,
              }
            : {};
          await hazardGateway.update(hazard.id, {
            ...imagePatch,
            last_synced: syncTime,
          });
          if (serverTags.length > 0 && customTags.length > 0) {
            const merged = Array.from(new Set([...serverTags, ...customTags]));
            await hazardGateway.queueUpdate(hazard.id, {
              id: hazard.id,
              tags: merged,
              tag_source: 'server',
            });
          }
          if (serverHazardData?.image_data) {
            try {
              eventEmitter.emit(EVENTS.HAZARD_UPDATED, {
                ...hazard,
                image_data: serverHazardData.image_data,
                image_count: serverHazardData.image_count || serverHazardData.image_data.length,
                media: serverHazardData.media,
                last_synced: syncTime,
                deleted: false
              });
            } catch (emitErr) {
              console.warn('Failed to emit HAZARD_UPDATED after handleSubmit response:', emitErr);
            }
          }
        } catch (updateError) {
          console.warn(`Could not update local cache with blob URLs for hazard ${hazard.id}:`, updateError);
        }
      }
      return true;
    }

    // Enhanced error handling for conflict resolution
    const errorText = await response.text();
    let errorData;
    try {
      errorData = JSON.parse(errorText);
    } catch {
      errorData = { message: errorText };
    }

    // A soft-delete outbox op (`hazard.delete`) that gets 404 is already gone
    // on the server (permanent delete, or the row never landed). Finish it.
    if (response.status === 404 && hazard.deleted) {
      const syncTime = moment().format();
      try {
        await hazardGateway.update(hazard.id, {
          deleted: true,
          last_synced: syncTime
        });
      } catch (updateError) {
        console.warn(`[DELETION] Could not finish already-removed hazard ${hazard.id}:`, updateError);
      }
      return true;
    }

    console.error(`Server error syncing ${hazard.deleted ? 'deletion of' : ''} hazard ${hazard.id}:`, response.status, response.statusText, errorData);

    // Handle specific error cases for better conflict resolution
    if (response.status === 404) {
      // Create path: the row was never synced. Keep it for retry.
      console.log(`[DELETION] 🔍 Hazard ${hazard.id} not found on server, keeping it for retry`);
      await recordFailedHazardAttempt(hazard.id);
    } else if (response.status === 401) {
      await recordFailedHazardAttempt(hazard.id, { pause: true });
    } else if (response.status === 409) {
      // UUID collision - this should not happen with proper UUID generation.
      // Terminal: retrying will always fail.
      console.error(`UUID collision for hazard ${hazard.id} - this should not happen`);
      await recordFailedHazardAttempt(hazard.id, {
        syncError: 'UUID_COLLISION',
        terminal: true,
        changes: { last_synced: syncTime },
      });
    } else if (response.status === 400 || response.status === 413) {
      // Terminal client errors (malformed payload / media too large): retrying
      // the same submission cannot succeed, so dead-letter the row.
      console.error(`Terminal client error (HTTP ${response.status}) for hazard ${hazard.id} - dead-lettering`);
      await recordFailedHazardAttempt(hazard.id, { syncError: `HTTP_${response.status}`, terminal: true });
    } else {
      // Retryable server errors (5xx, 429, ...) - keep for retry
      console.log(`Keeping hazard ${hazard.id} for retry due to server error ${response.status}`);
      await recordFailedHazardAttempt(hazard.id);
    }
    return false;
  } catch (error) {
    // If network error, keep deletion pending for retry
    console.error(`Network error submitting ${hazard.deleted ? 'deletion of' : ''} hazard ${hazard.id}:`, error);
    // Retryable: bump the attempt counter (dead-letters after the budget is spent).
    await recordFailedHazardAttempt(hazard.id);
    // For new hazards, sync status was never set, so no need to revert
    return false;
  }
}

// Store the current user's card reactions. Other people's rows are not written.
async function processIncidentReactions(hazards: Hazard[]): Promise<void> {
  try {
    const currentSyncTime = moment().format();
    const currentUserId = getCurrentUserId();
    const reactionsToStore: Reaction[] = [];
    const keepIds = new Set<string>();

    for (const hazard of hazards) {
      const hazardReactions = hazard.reactions;
      if (!Array.isArray(hazardReactions)) {
        continue;
      }
      for (const reaction of hazardReactions) {
        if (!reaction?.id) {
          continue;
        }
        if (currentUserId && reaction.user_id && reaction.user_id !== currentUserId) {
          continue;
        }
        keepIds.add(reaction.id);
        reactionsToStore.push({
          id: reaction.id,
          user_id: reaction.user_id,
          reaction_type_name: reaction.reaction_type_name,
          content_type: 'card',
          content_id: hazard.id,
          created_at: reaction.created_at || currentSyncTime,
          deleted: false,
          last_synced: currentSyncTime,
          local_only: false,
        });
      }
    }

    if (reactionsToStore.length > 0) {
      logger.debug(LogCategory.REACTIONS, `PROCESS_REACTIONS: Storing ${reactionsToStore.length} reactions from ${hazards.length} incidents`);
      await reactionTable.bulkPut(reactionsToStore);
    }

    for (const hazard of hazards) {
      const rows = await reactionTable
        .filter(r => r.content_type === 'card' && r.content_id === hazard.id)
        .toArray();
      for (const row of rows) {
        if (row.local_only || keepIds.has(row.id)) {
          continue;
        }
        await reactionTable.delete(row.id);
      }
    }
  } catch (error) {
    console.error('Error processing incident reactions:', error);
  }
}

// Helper function to push a single unsynced comment to the server
async function pushSingleCommentSubmission(comment: Comment): Promise<boolean> {
  console.log(`🔄 Attempting to sync comment ${comment.id}...`);
  
  // Check if comment needs syncing
  if (!comment || comment.last_synced) {
    console.log(`⏭️ Skipping comment ${comment.id} - already synced or invalid`);
    return true; // Consider it successful if no push needed
  }
  
  // Skip deleted comments that don't have proper deletion flag
  if (comment.deleted === undefined) {
    console.warn(`⚠️ Skipping comment ${comment.id} - deleted status undefined`);
    return true;
  }

  if (!navigator.onLine) {
    console.log(`📴 Cannot sync comment ${comment.id} - offline`);
    return false;
  }
  
  console.log(`📤 Syncing comment ${comment.id} to server...`);
  const syncTime = moment().format();

  // STEP 3: Prepare payload and make API call
  // Send media references to server:
  // - data: URLs (legacy)
  // - http(s) blob URLs (direct-to-Blob upload)
  // Do NOT send blob: object URLs (not valid outside this browser session)
  let imagesForSync: string[] = [];
  if (comment.image_data && comment.image_data.length > 0 && !comment.deleted) {
    imagesForSync = normalizeMediaRefsForSync(comment.image_data);
    if (imagesForSync.length === 0) {
      await commentTable.update(comment.id!, { local_only: true, last_synced: undefined });
      console.warn(`Skipping comment ${comment.id} sync - no valid media references for server payload`);
      return false;
    }
  }
  
  const payload = comment.deleted ? 
    { id: comment.id } : // Send object with id for deletion
    {
      // Full payload for normal submissions
      id: comment.id, // Include the UUID in the payload
      incident_id: comment.incident_id,
      comment: comment.comment,
      created_at: comment.created_at,
      updated_at: comment.updated_at || comment.created_at,
      images: imagesForSync, // Include images as data URLs for server
      is_resolution: comment.is_resolution ?? false
    };
  
  // STEP 4: Make API call and wait for completion
  try {
    const endpoint = comment.deleted ? `${API_BASE_URL}/api/softDeleteComment` : `${API_BASE_URL}/api/submitComment`;
    
    console.log(`📡 Sending comment ${comment.id} to ${endpoint}...`);
    
    const response = await fetchWithAuth(endpoint, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    
    if (response.ok) {
      console.log(`✅ Comment ${comment.id} successfully synced to server`);
      
      // If this was a new comment, try to get server ID from response
      if (!comment.deleted) {
        try {
          const responseData = await response.json();
          if (responseData && responseData.id) {
            // Update with server data including Azure Blob Storage URLs
            const updateData: Partial<Comment> = {
              id: responseData.id,
              last_synced: syncTime,
              local_only: false
            };
            
            // Update image_data with Azure Blob Storage URLs from server
            if (responseData.image_data) {
              updateData.image_data = responseData.image_data;
              updateData.image_count = responseData.image_count || responseData.image_data.length;
            }
            
            await commentTable.update(comment.id!, updateData);
          }
        } catch (e) {
          // If we can't get server ID, that's okay, we already marked it as synced
          console.warn(`Could not get server ID for comment ${comment.id}:`, e);
        }
        // Mark as synced after success (if not updated above) and notify UI
        await commentTable.update(comment.id!, {
          last_synced: syncTime,
          local_only: false,
          sync_action: undefined,
          pending_added_images: undefined,
          pending_deleted_image_indexes: undefined
        });
        eventEmitter.emit(EVENTS.COMMENT_SYNCED, comment.incident_id, comment.id);
        eventEmitter.emit(EVENTS.REACTIONS_SYNCED, comment.id);
      } else {
        // Deletion succeeded - remove tombstone locally
        await commentTable.delete(comment.id!);
      }
      return true;
    }

    // If API call failed, revert the sync status
    console.error(`❌ Failed to submit comment ${comment.id}, server responded with ${response.status}: ${response.statusText}`);
    await commentTable.update(comment.id!, { last_synced: undefined, local_only: true });
    // Keep pending; UI remains unchanged
    return false;
  } catch (error) {
    // If network error, revert the sync status
    console.error(`❌ Network error submitting comment ${comment.id}:`, error);
    await commentTable.update(comment.id!, { last_synced: undefined, local_only: true });
    // Keep pending; UI remains unchanged
    return false;
  }
}

class HazardService {
  private globalCommentListenerSetup = false;


  constructor() {
    // Set up global comment storage listener (runs even when CommentSection is unmounted)
    this.setupGlobalCommentStorage();
  }

  /**
   * Set up a global listener for COMMENT_ADDED events to store comments in IndexedDB
   * even when CommentSection components are unmounted (e.g., when comments are hidden)
   */
  private setupGlobalCommentStorage(): void {
    if (this.globalCommentListenerSetup) {
      return; // Already set up
    }

    console.log('[HazardService] Setting up global comment storage listener');
    
    const handleCommentAdded = async (comment: Comment) => {
      if (!comment.id || !comment.incident_id) {
        console.warn('[HazardService] Comment missing required fields, skipping storage:', comment);
        return;
      }

      try {
        console.log(`[HazardService] 📥 Storing comment ${comment.id} from WebSocket globally`);
        
        const commentToStore: Comment = {
          id: comment.id,
          incident_id: comment.incident_id,
          user_id: comment.user_id || '',
          username: comment.username,
          profileImageUrl: comment.profileImageUrl,
          comment: comment.comment || '',
          created_at: comment.created_at || new Date().toISOString(),
          updated_at: comment.updated_at || comment.created_at || new Date().toISOString(),
          deleted: comment.deleted || false,
          local_only: false, // This is from server/WebSocket
          last_synced: comment.updated_at || comment.created_at || new Date().toISOString(),
          image_data: comment.image_data || [],
          image_count: comment.image_count || 0
        };
        userProfileService.rememberUsername(commentToStore.user_id, commentToStore.username);
        
        try {
          await commentTable.put(commentToStore);
          console.log(`[HazardService] ✅ Stored comment ${comment.id} in IndexedDB (global handler)`);
        } catch (putError: any) {
          // If put() fails, try add() instead
          try {
            await commentTable.add(commentToStore);
            console.log(`[HazardService] ✅ Added comment ${comment.id} using add() method (global handler)`);
          } catch (addError: any) {
            // If add() also fails, it might already exist - try update()
            if (addError.name === 'ConstraintError' || addError.message?.includes('already exists')) {
              console.log(`[HazardService] Comment ${comment.id} already exists, updating instead (global handler)`);
              const { id: _commentId, ...commentChanges } = commentToStore;
              await commentTable.update(comment.id, commentChanges);
              console.log(`[HazardService] ✅ Updated existing comment ${comment.id} (global handler)`);
            } else {
              throw addError;
            }
          }
        }
      } catch (error) {
        console.error(`[HazardService] ❌ Error storing comment ${comment.id} globally:`, error);
      }
    };

    const handleCommentUpdated = async (updatedComment: Comment, source: 'local' | 'server' = 'server') => {
      if (!updatedComment.id) {
        return;
      }

      try {
        const isLocalUpdate = source === 'local';
        await commentTable.update(updatedComment.id, {
          ...updatedComment,
          local_only: isLocalUpdate,
          last_synced: isLocalUpdate
            ? undefined
            : (updatedComment.updated_at || updatedComment.created_at || new Date().toISOString())
        });
        console.log(`[HazardService] ✅ Updated comment ${updatedComment.id} globally`);
      } catch (error) {
        console.error(`[HazardService] ❌ Error updating comment ${updatedComment.id} globally:`, error);
      }
    };

    const handleCommentDeleted = async (incidentId: string, commentId: string) => {
      if (!commentId) {
        return;
      }

      try {
        await commentTable.delete(commentId);
        console.log(`[HazardService] ✅ Deleted comment ${commentId} globally`);
      } catch (error) {
        console.error(`[HazardService] ❌ Error deleting comment ${commentId} globally:`, error);
      }
    };

    // Register global listeners (these persist even when components unmount)
    eventEmitter.on(EVENTS.COMMENT_ADDED, handleCommentAdded);
    eventEmitter.on(EVENTS.COMMENT_UPDATED, handleCommentUpdated);
    eventEmitter.on(EVENTS.COMMENT_DELETED, handleCommentDeleted);

    this.globalCommentListenerSetup = true;
    console.log('[HazardService] ✅ Global comment storage listeners registered');
  }

  async syncHazards(): Promise<Hazard[]> {
    try {
      // Get last sync timestamp (now using lastCommunitySync for user hazards too)
      const lastSyncTimestamp = await readSyncMeta('lastCommunitySync');
      let fetchUrl = `${API_BASE_URL}/api/getUserIncidents`;
      let sinceTimestamp: number | undefined;
      
      if (lastSyncTimestamp) {
        // Convert ISO string to UNIX timestamp (seconds)
        sinceTimestamp = Math.floor(new Date(lastSyncTimestamp).getTime() / 1000);
        fetchUrl = `${fetchUrl}?since=${sinceTimestamp}`;
      }

      const response = await fetchWithAuth(fetchUrl);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch hazards: ${response.statusText}`);
      }

      const data = await response.json();
      const hazards: Hazard[] = data;
      for (const hazard of hazards) {
        userProfileService.rememberUsername(hazard.user_id || '', hazard.username);
      }
      // getUserIncidents rows are thin, so this pull does not write the cache.
      // The community feed is filled by the metadata path.
      await writeSyncMeta('lastCommunitySync', moment().format());

      return hazards;
    } catch (error) {
      console.error('Error syncing hazards:', error);
      throw error;
    }
  }

  // Fetch a single hazard's comment count from the server without loading all comments
  async getCommentCountForHazardFromServer(hazardId: string): Promise<number | null> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/getAllIncidents?limit=1&hazardId=${encodeURIComponent(hazardId)}`);
      if (!response.ok) {
        return null;
      }
      const hazards: Hazard[] = await response.json();
      if (hazards && hazards.length > 0) {
        const count = (hazards[0] as any).comment_count as number | undefined;
        return typeof count === 'number' ? count : null;
      }
      return null;
    } catch (error) {
      console.warn(`Failed to fetch server comment count for hazard ${hazardId}:`, error);
      return null;
    }
  }

  async getLocalHazards(): Promise<Hazard[]> {
    try {
      // Get hazards for the current user from communityHazards
      const currentUserId = await this.getCurrentUserId();
      if (!currentUserId) {
        return [];
      }
      const rows = await hazardGateway.list();
      return rows.filter((h) => h.user_id === currentUserId);
    } catch (error) {
      console.error('Error getting local hazards:', error);
      throw error;
    }
  }

  async getHazardById(id: string): Promise<Hazard | undefined> {
    try {
      return await hazardGateway.get(id);
    } catch (error) {
      console.error('Error getting hazard by id:', error);
      throw error;
    }
  }

  async addHazardToCommunityTable(hazard: Hazard): Promise<void> {
    try {
      // Ensure proper field mapping for consistent display
      const hazardWithCounts = {
        ...hazard,
        image_count: hazard.image_data ? hazard.image_data.length : 0,
        comment_count: hazard.comment_count || 0
      };
      await hazardGateway.put({
        ...hazardWithCounts,
        last_synced: hazard.last_synced ?? new Date().toISOString(),
      });
    } catch (error) {
      console.error('Error adding hazard to community table:', error);
      throw error;
    }
  }

  async getCommunityHazardById(id: string): Promise<Hazard | undefined> {
    try {
      return await hazardGateway.get(id);
    } catch (error) {
      console.error('Error getting community hazard by id:', error);
      throw error;
    }
  }

  async fetchAndUpdateHazardWithImages(hazardId: string): Promise<void> {
    const startedAt = Date.now();
    try {
      // Fetch complete hazard data from API
      const response = await fetchWithAuth(`${API_BASE_URL}/api/getAllIncidents?limit=1&hazardId=${hazardId}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch hazard data: ${response.statusText}`);
      }
      
      const hazards: Hazard[] = await response.json();
      
      if (hazards.length === 0) {
        throw new Error(`Hazard ${hazardId} not found in API response`);
      }
      
      const completeHazard = hazards[0];
      const newerResolved = newerResolvedThan(hazardId, startedAt);
      const resolvedFlag = newerResolved !== undefined
        ? newerResolved
        : !!(completeHazard as { resolved?: boolean }).resolved;
      
      // The hazard cache omits last_synced, so a missing timestamp is not an
      // unsynced create. Only a pending or failed outbox op must be kept —
      // put() would mark that op synced and drop photos that have not uploaded.
      const existingLocal = await hazardGateway.get(hazardId);
      const unsyncedLocal = existingLocal?.outbox_status === 'pending'
        || existingLocal?.outbox_status === 'failed';
      if (existingLocal && unsyncedLocal) {
        // Resolution is server-owned once this id exists there — discarding it
        // left the creator on "Resolve" after someone else resolved it.
        if (resolvedFlag !== !!existingLocal.resolved) {
          await hazardGateway.update(hazardId, { resolved: resolvedFlag });
        }
        await hazardGateway.markViewed(hazardId);
        return;
      }

      // Use put() instead of update() to create the record if it doesn't exist
      // This ensures hazards fetched from the server are properly stored locally
      await hazardGateway.put({
        ...completeHazard,
        id: hazardId, // Ensure ID is set
        image_count: completeHazard.image_count || (completeHazard.image_data ? completeHazard.image_data.length : 0),
        comment_count: completeHazard.comment_count || 0,
        resolved: resolvedFlag,
        deleted: completeHazard.deleted ?? false,
        last_synced: moment().format()
      });
      await hazardGateway.markViewed(hazardId);
      
    } catch (error) {
      console.error(`Error fetching and updating hazard ${hazardId}:`, error);
      throw error;
    }
  }

  // OPTIMIZED: Submit new hazard - FAST UI-first approach
  async submitNewHazard(hazardData: Omit<Hazard, 'id' | 'last_synced'> & {
    tags?: string[];
    mediaFiles?: File[];
    customTags?: string[];
  }): Promise<string> {
    if (isTenantDisabledNow()) {
      throw new Error("This organization's Safework access is not enabled or has expired.");
    }
    // STEP 1: Perform action - Generate ID and create hazard object
    const generatedId = crypto.randomUUID().toUpperCase();
    const currentUserId = await this.getCurrentUserId();
    
    // Normalize media inputs to data URLs for persistent display + server sync.
    // New flow sends full data URLs (image/* or video/*). Older flow may send raw base64 images.
    const dataUrls: string[] = (hazardData.image_data || []).map((item) => {
      if (typeof item !== 'string') return '';
      if (item.startsWith('data:')) return item;
      // New flow: direct-to-Blob uploads provide http(s) URLs
      if (item.startsWith('http://') || item.startsWith('https://')) return item;
      return `data:image/jpeg;base64,${item}`;
    }).filter(Boolean);
    
    const hazardDataToAdd: Hazard = {
      id: generatedId,
      description: hazardData.description,
      image_data: hazardData.mediaFiles?.length ? [] : dataUrls,
      utc_timestamp: hazardData.utc_timestamp,
      latitude: hazardData.latitude,
      longitude: hazardData.longitude,
      last_synced: undefined,
      tags: hazardData.tags || [],
      tag_source: hazardData.tag_source
    };

    try {
      // STEP 1: Store locally in communityHazards table (single fast operation)
      // CRITICAL: Use data URLs for image_data for persistent display across navigation, include image_count and comment_count for proper display
      const currentUsername = await userProfileService.resolveCurrentUsername();

      if (currentUserId) {
        userProfileService.rememberUsername(currentUserId, currentUsername);
        await hazardGateway.addLocal({
          ...hazardDataToAdd,
          image_data: hazardData.mediaFiles?.length ? [] : dataUrls,
          user_id: currentUserId,
          username: currentUsername || undefined,
          image_count: hazardData.mediaFiles?.length ? hazardData.mediaFiles.length : dataUrls.length,
          comment_count: 0,
          deleted: false
        }, {
          files: hazardData.mediaFiles,
          customTags: hazardData.customTags,
        });
      }

      // STEP 2: Sync. Files are stored as media:<id> refs by addLocal; posting
      // image_data: dataUrls here would send [] and drop the photo. Drain the
      // outbox so pushUnsyncedHazards resolves those refs, SAS-uploads them,
      // and posts the blob URLs. Description-only creates still go through
      // syncHazardAsync. Do not do both — that would POST the same create twice.
      if (hazardData.mediaFiles && hazardData.mediaFiles.length > 0) {
        void import('./outboxDrain').then(({ drainOutbox }) => drainOutbox()).catch((error: unknown) => {
          console.error('Error draining outbox:', error);
        });
      } else {
        const hazardForSync = {
          ...hazardDataToAdd,
          user_id: currentUserId || undefined,
          image_data: dataUrls // Send data URLs so backend can detect mime type
        };
        this.syncHazardAsync(hazardForSync, 'CREATE');
      }

      // STEP 3: End response
      return generatedId;
    } catch (error) {
      console.error('Error saving hazard locally:', error);
      throw new Error('Failed to save hazard locally.');
    }
  }

  // OPTIMIZED: Add comment - FAST UI-first approach
  async addComment(hazardId: string, commentText: string, images?: File[]): Promise<string> {
    try {
      // STEP 1: Perform action - Create comment object
      const userId = await this.getCurrentUserId();
      if (!userId) {
        throw new Error("User not authenticated");
      }
      
      // Get username for immediate local display
      const username = await userProfileService.resolveCurrentUsername() || undefined;
      userProfileService.rememberUsername(userId, username);
      
      // Get profile image URL for immediate local display
      let profileImageUrl: string | undefined = undefined;
      try {
        const userProfile = await userProfileService.getUserProfile();
        profileImageUrl = userProfile.profileImageUrl;
      } catch (error) {
        // If profile fetch fails, continue without profile image
        // It will be populated after server sync
        logger.authWarn('Failed to fetch user profile for comment creation', error);
      }
      
      const now = moment().format();
      const commentId = crypto.randomUUID().toUpperCase();
      
      // Process images if provided
      let imageData: string[] = [];
      let imageCount = 0;
      
      if (images && images.length > 0) {
        await assertStorageForFiles(images);
        imageCount = images.length;
      }
      
      const comment: Comment = {
        id: commentId,
        incident_id: hazardId,
        user_id: userId,
        username: username,
        profileImageUrl: profileImageUrl,
        comment: commentText,
        created_at: now,
        updated_at: now,
        deleted: false,
        last_synced: undefined,
        local_only: true,
        image_data: imageData,
        image_count: imageCount,
        is_resolution: false
      };
      
      // STEP 1: Store locally (single fast operation)
      await commentTable.add(comment);
      const hazardOp = await hazardGateway.findOpByEntity(hazardId);
      await hazardGateway.enqueueUserOp({
        kind: 'comment.create',
        entityId: commentId,
        payload: { ...comment },
        dependsOn: hazardOp?.kind === 'hazard.create' ? hazardOp.id : undefined,
        files: images,
      });
      const queued = await hazardGateway.findOpByEntity(commentId);
      const queuedImages = queued?.payload.image_data;
      const refs = Array.isArray(queuedImages)
        ? queuedImages.filter((item): item is string => typeof item === 'string')
        : [];
      if (refs.length > 0) {
        await commentTable.update(commentId, { image_data: refs, image_count: refs.length });
        comment.image_data = refs;
        comment.image_count = refs.length;
      }
      const displayRefs = await hazardGateway.resolveMediaRefs(comment.image_data ?? []);
      eventEmitter.emit(EVENTS.COMMENT_ADDED, { ...comment, image_data: displayRefs });
      void import('./outboxDrain').then(({ drainOutbox }) => drainOutbox()).catch((error: unknown) => {
        console.error('Error draining outbox:', error);
      });
      return commentId;
    } catch (error) {
      console.error('Error adding comment:', error);
      throw error;
    }
  }
  
  // OPTIMIZED: Update comment - FAST UI-first approach
  async updateComment(commentId: string, newText: string, addedImages?: File[], deletedImageIndexes?: number[]): Promise<void> {
    try {
      // STEP 1: Get comment and validate ownership
      const comment = await commentTable.get(commentId);
      if (!comment) {
        throw new Error(`Comment with ID ${commentId} not found`);
      }

      const userId = await this.getCurrentUserId();
      if (!userId || comment.user_id !== userId) {
        throw new Error("You can only edit your own comments");
      }

      // STEP 2: Process image changes
      let currentImageData = comment.image_data || [];
      let newImageCount = currentImageData.length;
      
      // Handle image deletions
      if (deletedImageIndexes && deletedImageIndexes.length > 0) {
        // Remove images at specified indexes (in reverse order to maintain indexes)
        const sortedIndexes = [...deletedImageIndexes].sort((a, b) => b - a);
        for (const index of sortedIndexes) {
          if (index >= 0 && index < currentImageData.length) {
            currentImageData.splice(index, 1);
          }
        }
        newImageCount = currentImageData.length;
      }
      
      if (addedImages && addedImages.length > 0) {
        await assertStorageForFiles(addedImages);
        newImageCount = currentImageData.length + addedImages.length;
      }

      const now = moment().format();
      const updatedComment: Comment = {
        ...comment,
        comment: newText,
        updated_at: now,
        last_synced: undefined,
        local_only: true,
        image_data: currentImageData,
        image_count: newImageCount,
        is_resolution: comment.is_resolution ?? false
      };
      await commentTable.update(commentId, {
        comment: newText,
        updated_at: now,
        last_synced: undefined,
        local_only: true,
        image_data: currentImageData,
        image_count: newImageCount,
      });

      const existingOp = await hazardGateway.findOpByEntity(commentId);
      if (existingOp?.kind === 'comment.create') {
        await hazardGateway.enqueueUserOp({
          kind: 'comment.create',
          entityId: commentId,
          payload: {
            ...updatedComment,
            image_data: currentImageData,
          },
          files: addedImages,
        });
      } else {
        if (addedImages && addedImages.length > 0) {
          await assertStorageForFiles(addedImages);
        }
        await hazardGateway.enqueueUserOp({
          kind: 'comment.update',
          entityId: commentId,
          payload: {
            ...updatedComment,
            deleted_image_indexes: deletedImageIndexes || [],
          },
          files: addedImages,
        });
      }
      eventEmitter.emit(EVENTS.COMMENT_UPDATED, updatedComment, 'local');
      void import('./outboxDrain').then(({ drainOutbox }) => drainOutbox()).catch((error: unknown) => {
        console.error('Error draining outbox:', error);
      });
    } catch (error) {
      console.error('Error updating comment:', error);
      throw error;
    }
  }

  // OPTIMIZED: Delete comment - FAST UI-first approach
  async deleteComment(commentId: string): Promise<void> {
    try {
      // STEP 1: Get comment and validate ownership
      const comment = await commentTable.get(commentId);
      if (!comment) {
        throw new Error(`Comment with ID ${commentId} not found`);
      }
      
      const userId = await this.getCurrentUserId();
      if (!userId || comment.user_id !== userId) {
        throw new Error("You can only delete your own comments");
      }

      const existingCreate = await hazardGateway.findOpByEntity(commentId);
      if (existingCreate?.kind === 'comment.create') {
        await hazardGateway.dropOpForEntity(commentId);
        await commentTable.delete(commentId);
      } else {
        await commentTable.update(commentId, {
          deleted: true,
          last_synced: undefined,
          local_only: true
        });
        await hazardGateway.enqueueUserOp({
          kind: 'comment.delete',
          entityId: commentId,
          payload: { id: commentId, incident_id: comment.incident_id, deleted: true },
        });
        void import('./outboxDrain').then(({ drainOutbox }) => drainOutbox()).catch((error: unknown) => {
          console.error('Error draining outbox:', error);
        });
      }
      
      // STEP 2: Update UI immediately (synchronous)
      eventEmitter.emit(EVENTS.COMMENT_DELETED, comment.incident_id, commentId);
    } catch (error) {
      console.error('[DELETION] ❌ Error deleting comment:', error);
      throw error;
    }
  }

  // OPTIMIZED: Soft delete hazard - FAST UI-first approach with enhanced event handling
  async softDeleteHazard(hazardId: string): Promise<boolean> {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        throw new Error("User not authenticated");
      }

      // STEP 1: Get the hazard to verify ownership
      const hazard = await hazardGateway.get(hazardId);
      
      if (!hazard) {
        throw new Error("Hazard not found");
      }

      // STEP 2: Mark hazard as deleted locally (immediate UI update)
      await hazardGateway.update(hazardId, { 
        deleted: true, 
        last_synced: undefined // Mark as needing sync
      });

      // STEP 3: Update UI immediately with enhanced event data
      const eventData = {
        id: hazardId,
        user_id: userId,
        deleted: true,
        deleted_by: userId,
        deleted_at: new Date().toISOString(),
        description: hazard.description,
        latitude: hazard.latitude,
        longitude: hazard.longitude,
        utc_timestamp: hazard.utc_timestamp
      };
      
      // Emit deletion event only (no duplicate HAZARD_UPDATED event)
      eventEmitter.emit(EVENTS.HAZARD_DELETED, hazardId, eventData);

      // STEP 5: Sync with server immediately (blocking for deletions to ensure backend is updated)
      console.log(`[DELETION] 🗑️ Syncing deletion of hazard ${hazardId} to backend...`);
      
      if (!navigator.onLine) {
        console.warn(`[DELETION] ⚠️ Hazard ${hazardId} marked for deletion but will sync when back online`);
        return true;
      }

      // Perform immediate synchronous backend deletion
      try {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/softDeleteHazard`, {
          method: 'POST',
          body: JSON.stringify({ id: hazardId }),
        });

        if (response.ok) {
          const syncTime = moment().format();
          console.log(`[DELETION] ✅ Successfully deleted hazard ${hazardId} on backend`);
          
          // Mark as synced in communityHazards table
          try {
            await hazardGateway.update(hazardId, { 
              deleted: true, 
              last_synced: syncTime 
            });
          } catch (updateError) {
            console.warn(`[DELETION] ⚠️ Could not update sync status for deleted hazard ${hazardId}:`, updateError);
          }
        } else {
          console.error(`[DELETION] ❌ Backend deletion failed for hazard ${hazardId}:`, response.status, response.statusText);
          throw new Error(`Backend deletion failed with status ${response.status}`);
        }
      } catch (syncError) {
        console.error(`[DELETION] ❌ Error syncing deletion of hazard ${hazardId} to backend:`, syncError);
        // Don't throw - allow the local deletion to succeed and retry sync later
        // The hazard will be synced by the periodic sync mechanism
      }

      return true;
    } catch (error) {
      console.error('[DELETION] ❌ Error soft deleting hazard:', error);
      throw error;
    }
  }

  // Mark hazard as resolved - robust UI-first approach with enhanced error handling
  async resolveHazard(hazardId: string, resolved: boolean = true): Promise<boolean> {
    noteHazardResolved(hazardId, resolved);
    try {
      // Store original state for potential rollback
      let originalHazard = await hazardGateway.get(hazardId);
      
      // If hazard not found locally, try to fetch from server first
      if (!originalHazard) {
        console.warn(`Hazard ${hazardId} not found in local database, attempting to fetch from server...`);
        
        try {
          const serverHazard = await this.fetchHazardFromServer(hazardId);
          if (serverHazard) {
            // Store the fetched hazard locally
            await hazardGateway.put(serverHazard);
            
            originalHazard = serverHazard;
            console.log(`Successfully fetched and stored hazard ${hazardId} from server`);
          } else {
            console.error(`Hazard ${hazardId} not found on server either`);
            return false;
          }
        } catch (error) {
          console.error(`Failed to fetch hazard ${hazardId} from server:`, error);
          return false;
        }
      }

      // Optimistically update local DB first (user state is priority)
      await hazardGateway.queueResolve(hazardId, resolved);
      await hazardGateway.update(hazardId, { resolved });

      // Notify UI via event bus immediately
      const updated = await hazardGateway.get(hazardId);
      if (updated) {
        eventEmitter.emit(EVENTS.HAZARD_UPDATED, { ...updated, resolved });
      }

      // Send to server with retry logic
      let serverSuccess = false;
      let lastError: Error | null = null;
      
      // Retry up to 3 times for network/server issues
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          const response = await fetchWithAuth(`${API_BASE_URL}/api/resolveHazard`, {
            method: 'POST',
            body: JSON.stringify({ id: hazardId, resolved })
          });
          
          if (response.ok) {
            serverSuccess = true;
            console.log(`Hazard resolution successful on attempt ${attempt}`);
            break;
          } else {
            const errorData = await response.json().catch(() => ({}));
            lastError = new Error(`Server error: ${response.status} - ${errorData.error?.message || 'Unknown error'}`);
            
            // Don't retry on client errors (4xx)
            if (response.status >= 400 && response.status < 500) {
              console.error('Client error, not retrying:', lastError.message);
              break;
            }
            
            console.warn(`Server error on attempt ${attempt}, retrying...`, lastError.message);
          }
        } catch (error) {
          lastError = error instanceof Error ? error : new Error('Network error');
          console.warn(`Network error on attempt ${attempt}, retrying...`, lastError.message);
        }
        
        // Wait before retry (exponential backoff)
        if (attempt < 3) {
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
        }
      }

      if (serverSuccess) {
        await hazardGateway.update(hazardId, {
          resolved,
          last_synced: new Date().toISOString(),
        });
      }

      return true;
    } catch (error) {
      console.error('Error resolving hazard:', error);
      return false;
    }
  }

  // Fetch a single hazard from server by searching all incidents
  async fetchHazardFromServer(hazardId: string): Promise<any | null> {
    try {
      // One row by id. Without hazardId the API caps the list at the newest 50.
      const response = await fetchWithAuth(`${API_BASE_URL}/api/getAllIncidents?limit=1&hazardId=${encodeURIComponent(hazardId)}`, {
        method: 'GET',
      });
      
      if (response.ok) {
        const incidents = await response.json();
        const hazard = incidents.find((incident: any) => incident.id === hazardId);
        
        if (hazard) {
          console.log(`Fetched hazard ${hazardId} from server:`, hazard);
          return hazard;
        } else {
          console.log(`Hazard ${hazardId} not found in server incidents`);
          return null;
        }
      } else {
        console.error(`Failed to fetch incidents from server: ${response.status}`);
        return null;
      }
    } catch (error) {
      console.error(`Error fetching hazard ${hazardId} from server:`, error);
      return null;
    }
  }

  // Sync hazard resolution state with server - fallback mechanism
  async syncHazardResolutionState(hazardId: string): Promise<boolean> {
    try {
      const serverHazard = await this.fetchHazardFromServer(hazardId);
      
      if (serverHazard) {
        const serverResolved = !!serverHazard.resolved;
        
        // Update local state to match server
        await hazardGateway.update(hazardId, { resolved: serverResolved });
        
        // Notify UI of sync
        const syncedHazard = await hazardGateway.get(hazardId);
        if (syncedHazard) {
          eventEmitter.emit(EVENTS.HAZARD_UPDATED, { ...syncedHazard, resolved: serverResolved });
        }
        
        console.log(`Synced hazard ${hazardId} resolution state: ${serverResolved}`);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Failed to sync hazard resolution state:', error);
      return false;
    }
  }


  // OPTIMIZED: Update hazard - FAST UI-first approach with enhanced event handling
  async tagHazard(hazardId: string): Promise<boolean> {
    try {
      if (!navigator.onLine) {
        throw new Error('Cannot tag hazards while offline. You must be online to generate tags.');
      }

      logger.info(LogCategory.HAZARDS, `Triggering backend tagging for hazard ${hazardId}`);

      const response = await fetchWithAuth(`${API_BASE_URL}/api/tagHazard`, {
        method: 'POST',
        body: JSON.stringify({ incident_id: hazardId }),
      });

      if (!response.ok) {
        let errorMessage = 'Failed to generate tags';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error?.message || errorMessage;
        } catch (e) {
          // Keep default message
        }
        throw new Error(errorMessage);
      }

      // The backend will broadcast a hazard_tagged event, which eventStreamService will pick up.
      return true;
    } catch (error) {
      logger.error(LogCategory.HAZARDS, `Error tagging hazard ${hazardId}`, error);
      throw error;
    }
  }

  async updateHazard(hazardData: {
    id: string;
    description: string;
    latitude?: number;
    longitude?: number;
    image_data?: string[];
    deletedImageIndexes?: number[];
    tags?: string[];
  }): Promise<Hazard> {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        throw new Error("User not authenticated");
      }

      // STEP 1: Get the hazard to verify ownership
      const existingHazard = await hazardGateway.get(hazardData.id);
      if (!existingHazard) {
        throw new Error("Hazard not found");
      }

      // STEP 2: Update hazard locally (immediate UI update)
      const updatedHazardData: Partial<Hazard> = {
        description: hazardData.description,
        last_synced: undefined // Mark as needing sync
      };

      if (hazardData.tags !== undefined) {
        updatedHazardData.tags = hazardData.tags;
      }

      // Update coordinates if provided
      if (hazardData.latitude !== undefined && hazardData.longitude !== undefined) {
        updatedHazardData.latitude = hazardData.latitude;
        updatedHazardData.longitude = hazardData.longitude;
      }

      // Handle image updates based on user selection
      let finalImages: string[] = [];
      
      // Start with existing images, filtering out deleted ones
      const existingImages = existingHazard.image_data || [];
      const deletedIndexes = new Set(hazardData.deletedImageIndexes || []);
      
      // Keep existing images that weren't marked for deletion
      const remainingExistingImages = existingImages.filter((_, index) => !deletedIndexes.has(index));
      finalImages = [...remainingExistingImages];
      
      // Add new media if provided
      if (hazardData.image_data && hazardData.image_data.length > 0) {
        const newImageDataUrls: string[] = hazardData.image_data.map((item) => {
          if (typeof item !== 'string') return '';
          if (item.startsWith('data:')) return item;
          if (item.startsWith('http://') || item.startsWith('https://')) return item;
          return `data:image/jpeg;base64,${item}`;
        }).filter(Boolean);
        
        // Combine remaining existing images with new ones
        finalImages = [...finalImages, ...newImageDataUrls];
      }
      
      // Update the hazard with the final image array
      updatedHazardData.image_data = finalImages;
      updatedHazardData.image_count = finalImages.length;

      // STEP 2: Update in communityHazards table
      try {
        // Use modify() for in-place mutation; Table.update() does not accept a callback modifier.
        await hazardGateway.queueUpdate(hazardData.id, updatedHazardData);
      } catch (updateError) {
        console.warn(`Could not update communityHazards for edited hazard ${hazardData.id}:`, updateError);
      }

      // STEP 3: Update UI immediately with enhanced event data
      const updatedHazard = await hazardGateway.get(hazardData.id);
      if (updatedHazard) {
        const eventData = {
          ...updatedHazard,
          user_id: userId,
          updated_by: userId,
          updated_at: new Date().toISOString()
        };
        
        // Emit update events for comprehensive handling
        eventEmitter.emit(EVENTS.HAZARD_UPDATED, eventData);
      }

      // STEP 5: Sync with server (async, non-blocking)
      if (updatedHazard) {
        // Pass the image data and deleted indexes for proper server sync
        this.syncUpdatedHazardAsync(updatedHazard, hazardData.image_data, hazardData.deletedImageIndexes);
        return updatedHazard;
      }

      throw new Error('Failed to load updated hazard from local cache');
    } catch (error) {
      console.error('Error updating hazard:', error);
      throw error;
    }
  }

  // Helper method for syncing updated hazards
  private async syncUpdatedHazardAsync(hazard: Hazard, newImageData?: string[], deletedImageIndexes?: number[]): Promise<void> {
    // Sync the updated hazard to the server immediately (no setTimeout to avoid
    // race conditions with periodic getAllIncidents refreshes that can set
    // last_synced before the deferred callback fires).
    try {
      if (!navigator.onLine) {
        console.log(`Cannot sync updated hazard ${hazard.id} - offline`);
        return;
      }

      const payload: any = {
        id: hazard.id,
        description: hazard.description,
        latitude: hazard.latitude,
        longitude: hazard.longitude,
        tags: hazard.tags || []
      };
      
      // Handle image scenarios:
      // 1. Send new images if provided
      // 2. Send deleted image indexes if any images were deleted
      if (newImageData && newImageData.length > 0) {
        const normalizedImageData = normalizeMediaRefsForSync(newImageData);
        if (normalizedImageData.length === 0) {
          await hazardGateway.update(hazard.id, { sync_error: 'INVALID_MEDIA_REFS' });
          console.warn(`Skipping hazard update sync for ${hazard.id} - no valid media references for server payload`);
          return;
        }
        payload.image_data = normalizedImageData;
      }
      
      if (deletedImageIndexes && deletedImageIndexes.length > 0) {
        payload.deletedImageIndexes = deletedImageIndexes;
      }
      
      console.log(`Syncing updated hazard ${hazard.id} to server...`, {
        hasNewImages: !!(newImageData && newImageData.length > 0),
        newImageCount: newImageData?.length ?? 0,
        deletedIndexCount: deletedImageIndexes?.length ?? 0,
      });

      const response = await fetchWithAuth(`${API_BASE_URL}/api/updateHazard`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        // Process the server response to get updated image URLs
        try {
          const serverHazardData = await response.json();
          const syncTime = moment().format();
          
          if (serverHazardData && serverHazardData.image_data) {
            console.log(`Updating local cache with ${serverHazardData.image_data.length} blob URLs for updated hazard ${hazard.id}`);
            
            // Update communityHazards table with blob URLs for proper display
            await hazardGateway.update(hazard.id, {
              image_data: serverHazardData.image_data, // Server blob URLs
              image_count: serverHazardData.image_count || serverHazardData.image_data.length,
              media: serverHazardData.media,
              last_synced: syncTime
            });
            
            console.log(`Updated local cache with blob URLs for updated hazard ${hazard.id}`);
          } else {
            // No image data in response, just mark as synced
            const updates: any = { last_synced: syncTime };
            if (typeof serverHazardData.resolved === 'boolean') {
              updates.resolved = serverHazardData.resolved;
            }
            await hazardGateway.update(hazard.id, updates);
          }
        } catch (parseError) {
          console.warn(`Could not parse server response for updated hazard ${hazard.id}:`, parseError);
          // Mark as synced anyway
          const syncTime = moment().format();
          await hazardGateway.update(hazard.id, { last_synced: syncTime });
        }
      } else if (response.status === 404) {
        // Hazard doesn't exist on server - this means it was never synced
        // Convert this to a new submission instead of an update
        console.log(`Hazard ${hazard.id} not found on server, treating as new submission`);
        
        // Remove from communityHazards if it exists there (it shouldn't if it was never synced)
        try {
          await hazardGateway.remove(hazard.id);
        } catch (deleteError) {
          // Ignore if it doesn't exist
        }
        
        // The push list stamps last_synced on updates so they are not posted as creates.
        // This 404 means the row was never on the server, so submit it as a create.
        await pushSingleHazardSubmission({ ...hazard, last_synced: undefined });
      } else {
        console.error(`Server error updating hazard ${hazard.id}:`, response.status, response.statusText);
        // Mark with error for retry
        await hazardGateway.update(hazard.id, { 
          sync_error: `UPDATE_ERROR_${response.status}` 
        });
      }
    } catch (error) {
      console.error(`Network error updating hazard ${hazard.id}:`, error);
      try {
        await hazardGateway.update(hazard.id, { 
          sync_error: 'NETWORK_ERROR' 
        });
      } catch {
        // Ignore DB error during error handling
      }
    }
  }

  // Helper method to get current user ID - updated to use OIDC tokens
  private async getCurrentUserId(): Promise<string | null> {
    return getCurrentUserId();
  }

  // OPTIMIZED: Async sync method for hazards (non-blocking)
  private async syncHazardAsync(hazard: Hazard, operation: 'CREATE' | 'DELETE'): Promise<void> {
    // Run sync in background without blocking UI
    setTimeout(async () => {
      try {
        if (isTenantDisabledNow()) {
          return;
        }
        // Only use HTTP to send changes to the database
        if (navigator.onLine) {
          await pushSingleHazardSubmission(hazard);
        }
      } catch (error) {
        console.warn(`Background sync failed for hazard ${hazard.id}:`, error);
      }
    }, 0);
  }

  // Helper method for syncing updated comments
  private async syncUpdatedCommentAsync(comment: Comment, addedImageUrls?: string[], deletedImageIndexes?: number[]): Promise<boolean> {
    try {
      if (!navigator.onLine) {
        return false;
      }

      const addedImageData = normalizeMediaRefsForSync(addedImageUrls || []);
      const payload = {
        id: comment.id,
        comment: comment.comment,
        added_images: addedImageData,
        deleted_image_indexes: deletedImageIndexes || [],
        is_resolution: comment.is_resolution ?? false
      };

      const response = await fetchWithAuth(`${API_BASE_URL}/api/updateComment`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        // Mark as synced and update with server response data
        const syncTime = moment().format();
        
        try {
          const responseData = await response.json();
          const updateData: Partial<Comment> = { last_synced: syncTime, local_only: false, sync_action: undefined, pending_added_images: undefined, pending_deleted_image_indexes: undefined };
          
          // Update image_data with Azure Blob Storage URLs from server
          if (responseData.image_data) {
            updateData.image_data = responseData.image_data;
            updateData.image_count = responseData.image_count || responseData.image_data.length;
          }
          
          await commentTable.update(comment.id!, updateData);
        } catch (e) {
          // If we can't parse response, just mark as synced
          console.warn(`Could not parse server response for comment ${comment.id}:`, e);
          await commentTable.update(comment.id!, { last_synced: syncTime, local_only: false, sync_action: undefined, pending_added_images: undefined, pending_deleted_image_indexes: undefined });
        }
        eventEmitter.emit(EVENTS.COMMENT_SYNCED, comment.incident_id, comment.id);
        return true;
      }

      console.error(`Server error updating comment ${comment.id}:`, response.status, response.statusText);
      // Revert the sync status if server error
      await commentTable.update(comment.id!, { last_synced: undefined, local_only: true });
      return false;
    } catch (error) {
      console.error(`Network error updating comment ${comment.id}:`, error);
      await commentTable.update(comment.id!, { last_synced: undefined, local_only: true });
      return false;
    }
  }

  // Function to PUSH unsynced local changes to the server
  async pushUnsyncedHazards(): Promise<void> {
    if (isTenantDisabledNow()) {
      return;
    }
    if (!navigator.onLine) {
      console.log('📴 Offline - skipping hazard sync');
      return;
    }

    // Fetch all hazards for the current user and filter for those that need syncing
    const currentUserId = await this.getCurrentUserId();
    if (!currentUserId) {
      console.log('⚠️ No current user ID, skipping hazard sync');
      return;
    }
    
    const pending = await hazardGateway.listPendingHazards();
    const trulyUnsynced = pending.map(({ kind, hazard }) => {
      if (kind === 'hazard.delete') {
        return { ...hazard, deleted: true };
      }
      if (kind === 'hazard.update' || kind === 'hazard.resolve') {
        return { ...hazard, last_synced: hazard.last_synced ?? new Date(0).toISOString() };
      }
      return hazard;
    });

    if (trulyUnsynced.length === 0) {
      console.log('✅ No unsynced hazards found');
      return;
    }
    
    console.log(`🔄 Found ${trulyUnsynced.length} unsynced hazards to sync`);
    
    // Separate hazards by type and priority
    const deletedHazards: Hazard[] = [];
    const newHazards: Hazard[] = [];
    const updatedHazards: Hazard[] = [];
    const errorHazards: Hazard[] = [];
    
    for (const hazard of trulyUnsynced) {
      if (hazard.deleted) {
        deletedHazards.push(hazard);
      } else if (hazard.sync_error) {
        errorHazards.push(hazard);
      } else if (hazard.last_synced) {
        // This hazard was previously synced, so it's an update
        updatedHazards.push(hazard);
      } else {
        // This hazard has never been synced, so it's new
        newHazards.push(hazard);
      }
    }
    
    console.log(`📊 Sync breakdown: ${deletedHazards.length} deleted, ${newHazards.length} new, ${updatedHazards.length} updated, ${errorHazards.length} with errors`);
    
    // Process hazards in priority order: deleted > errors > new > updated
    const prioritizedHazards = [
      ...deletedHazards,
      ...errorHazards,
      ...newHazards.sort((a, b) => safeDateParse(b.utc_timestamp) - safeDateParse(a.utc_timestamp)),
      ...updatedHazards
    ];
    
    // Process hazards with controlled concurrency
    await this.processBatchWithConcurrency(prioritizedHazards, 3, async (hazard) => {
      try {
        if (hazard.deleted) {
          await pushSingleHazardSubmission(hazard);
        } else if (hazard.last_synced) {
          await this.syncUpdatedHazardAsync(hazard);
        } else {
          // MISSING_LOCATION is re-validated in-line by pushSingleHazardSubmission;
          // pre-clearing it would churn a write + re-render every cycle for a hazard
          // that still has no location. Other errors clear-and-retry as before.
          if (hazard.sync_error && hazard.sync_error !== 'MISSING_LOCATION') {
            await hazardGateway.update(hazard.id, { sync_error: undefined });
          }
          await pushSingleHazardSubmission(hazard);
        }
      } catch (error) {
        console.warn(`Could not sync hazard ${hazard.id}:`, error);
      }
    });
  }

  // Add this helper method for controlled concurrency
  private async processBatchWithConcurrency<T>(
    items: T[], 
    concurrency: number, 
    processor: (item: T) => Promise<void>
  ): Promise<void> {
    const queue = [...items];
    const inProgress: Promise<void>[] = [];
    
    while (queue.length > 0 || inProgress.length > 0) {
      // Fill up to concurrency limit
      while (inProgress.length < concurrency && queue.length > 0) {
        const item = queue.shift()!;
        const promise = processor(item).then(() => {
          // Remove this promise from inProgress when done
          const index = inProgress.indexOf(promise);
          if (index >= 0) inProgress.splice(index, 1);
        });
        inProgress.push(promise);
      }
      
      if (inProgress.length > 0) {
        // Wait for at least one promise to complete before continuing
        await Promise.race(inProgress);
      }
    }
  }

  /** Realtime-first fetch for full incident rows (getAllIncidents). */
  private async fetchAllIncidentsPage(pageSize: number, offset: number): Promise<Hazard[]> {
    let apiHazards: Hazard[] = [];

    if (eventStreamService.shouldUseRealtimeForRequests()) {
      try {
        apiHazards = await eventStreamService.getAllHazards(pageSize, offset);
      } catch (error) {
        console.warn('Realtime request failed, falling back to HTTP:', error);
      }
    }

    if (apiHazards.length === 0) {
      const fetchUrl = `${API_BASE_URL}/api/getAllIncidents?limit=${pageSize}&offset=${offset}`;
      const response = await fetchWithAuth(fetchUrl);

      if (!response.ok) {
        throw new Error(`Failed to fetch hazards: ${response.statusText}`);
      }

      apiHazards = await response.json();
    }

    return apiHazards;
  }

  private feedEtagKey(
    endpoint: 'getAllIncidents' | 'getIncidentsMetadata',
    limit: number,
    offset: number,
  ): string {
    return `etag:${endpoint}:${limit}:${offset}`;
  }

  private async readStoredFeedEtag(
    endpoint: 'getAllIncidents' | 'getIncidentsMetadata',
    limit: number,
    offset: number,
  ): Promise<string | undefined> {
    return readSyncMeta(this.feedEtagKey(endpoint, limit, offset));
  }

  private async storeFeedEtag(
    endpoint: 'getAllIncidents' | 'getIncidentsMetadata',
    limit: number,
    offset: number,
    etag: string | null,
  ): Promise<void> {
    if (!etag) {
      return;
    }
    await writeSyncMeta(this.feedEtagKey(endpoint, limit, offset), etag);
  }

  /** Page already stored locally, used when the server answers 304. */
  private async feedPageFromCache(limit: number, offset: number): Promise<Hazard[]> {
    return hazardGateway.feedSlice(offset, limit);
  }

  private normalizeMetadataRows(metadataHazards: Array<Record<string, unknown>>): Hazard[] {
    const syncTime = moment().format();
    return metadataHazards.map((meta) => {
      userProfileService.rememberUsername(String(meta.user_id || ''), String(meta.username || ''));
      return {
        ...meta,
        id: String(meta.id),
        utc_timestamp: String(meta.utc_timestamp ?? ''),
        latitude: Number(meta.latitude ?? 0),
        longitude: Number(meta.longitude ?? 0),
        image_data: Array.isArray(meta.image_data) ? (meta.image_data as string[]) : [],
        tags: Array.isArray(meta.tags) ? (meta.tags as string[]) : [],
        last_synced: syncTime,
      } as Hazard;
    });
  }

  /**
   * HTTP fetch with If-None-Match. 304 resolves from Dexie and sets notModified
   * so callers skip the upsert. 200 stores the new ETag.
   *
   * Search requests bypass the ETag cache entirely: their results are not
   * written to Dexie (see getHazardsMetadataFast), so a 304 could not be
   * resolved locally, and storing a search ETag under the shared feed key
   * would evict the feed page's ETag.
   */
  private async fetchFeedPage(
    endpoint: 'getAllIncidents' | 'getIncidentsMetadata',
    pageSize: number,
    offset: number,
    search?: { searchTerm?: string; searchField?: SearchField },
    options?: { bypassCache?: boolean }
  ): Promise<{ hazards: Hazard[]; notModified: boolean }> {
    const params = new URLSearchParams({
      limit: String(pageSize),
      offset: String(offset),
    });
    const isSearch = endpoint === 'getIncidentsMetadata' && !!(search?.searchTerm ?? '').trim();
    const bypassCache = options?.bypassCache === true || isSearch;
    if (endpoint === 'getIncidentsMetadata') {
      const term = search?.searchTerm?.trim() ?? '';
      if (term) {
        params.set('searchTerm', term);
        params.set('searchField', search?.searchField === 'tags' ? 'tags' : 'description');
      }
    }
    const fetchUrl = `${API_BASE_URL}/api/${endpoint}?${params.toString()}`;
    const storedEtag = bypassCache ? undefined : await this.readStoredFeedEtag(endpoint, pageSize, offset);
    const headers: Record<string, string> = {};
    if (storedEtag) {
      headers['If-None-Match'] = storedEtag;
    }
    const response = await fetchWithAuth(fetchUrl, { headers });

    if (response.status === 304) {
      const hazards = await this.feedPageFromCache(pageSize, offset);
      return { hazards, notModified: true };
    }

    if (!response.ok) {
      const label = endpoint === 'getAllIncidents' ? 'hazards' : 'hazards metadata';
      throw new Error(`Failed to fetch ${label}: ${response.statusText}`);
    }

    if (!bypassCache) {
      await this.storeFeedEtag(endpoint, pageSize, offset, response.headers.get('ETag'));
    }
    if (endpoint === 'getAllIncidents') {
      const hazards = (await response.json()) as Hazard[];
      return { hazards, notModified: false };
    }
    const metadataHazards = (await response.json()) as Array<Record<string, unknown>>;
    return { hazards: this.normalizeMetadataRows(metadataHazards), notModified: false };
  }

  /** HTTP fetch for lightweight incident metadata (getIncidentsMetadata). */
  private async fetchIncidentsMetadataPage(
    pageSize: number,
    offset: number,
    search?: { searchTerm?: string; searchField?: SearchField },
    options?: { bypassCache?: boolean }
  ): Promise<{ hazards: Hazard[]; notModified: boolean }> {
    return this.fetchFeedPage('getIncidentsMetadata', pageSize, offset, search, options);
  }


  async getAllHazards(): Promise<Hazard[]> {
    try {
      return await hazardGateway.list();
    } catch (error) {
      console.error('Error getting community hazards:', error);
      throw error;
    }
  }


  async getAllHazardsWithPagination(page = 1, pageSize = 10): Promise<{ 
    hazards: Hazard[];
    hasMore: boolean;
    totalCount?: number;
  }> {
    try {
      const offset = (page - 1) * pageSize;
      const apiHazards = await this.fetchAllIncidentsPage(pageSize, offset);
      await processIncidentReactions(apiHazards);

      const activeHazardsForResponse = apiHazards.filter(hazard => !hazard.deleted);
      const hasMore = apiHazards.length === pageSize;

      console.log(`getAllHazardsWithPagination: page=${page}, offset=${offset}, got ${apiHazards.length} hazards (${activeHazardsForResponse.length} active), hasMore=${hasMore}`);

      return {
        hazards: activeHazardsForResponse,
        hasMore,
        totalCount: apiHazards.length
      };
    } catch (error) {
      console.error('Error getting paginated community hazards:', error);
      throw error;
    }
  }

  // Get local community hazards with pagination (cached data only)
  async getLocalCommunityHazardsWithPagination(page = 1, pageSize = 10): Promise<{ 
    hazards: Hazard[];
    hasMore: boolean;
    totalCount?: number;
  }> {
    try {
      return await hazardGateway.feedPage(page, pageSize);
    } catch (error) {
      console.error('Error getting local community hazards with pagination:', error);
      throw error;
    }
  }

  /**
   * Online filter universe. Pages metadata in memory and never writes the
   * bounded feed window (etag bypassed so it cannot 304 against the window).
   */
  async fetchAllCommunityMetadata(): Promise<Hazard[]> {
    const pageSize = 200;
    const all: Hazard[] = [];
    let offset = 0;
    for (;;) {
      const page = await this.fetchIncidentsMetadataPage(pageSize, offset, undefined, { bypassCache: true });
      all.push(...page.hazards.filter((hazard) => !hazard.deleted));
      if (page.hazards.length < pageSize) {
        break;
      }
      offset += pageSize;
    }
    await processIncidentReactions(all);
    return all;
  }

  /**
   * The bounded window is maintained by feedSync. This no longer downloads
   * the whole dataset into the cache.
   */
  async ensureFullCommunitySync(_force = false): Promise<void> {
    return;
  }

  /**
   * Build the pick-lists for the filter panel (authors + regions) from the
   * locally-synced community hazards.
   */
  async getCommunityFilterFacets(source?: Hazard[]): Promise<CommunityFilterFacets> {
    try {
      const all = source ?? await hazardGateway.list();
      const userMap = new Map<string, string>();
      const regionSet = new Set<string>();
      for (const h of all) {
        if (h.user_id && !userMap.has(h.user_id)) {
          userMap.set(h.user_id, (h.username && h.username.trim()) || h.user_id);
        }
        if (h.region && h.region.trim()) regionSet.add(h.region.trim());
      }
      const users = Array.from(userMap.entries())
        .map(([userId, username]) => ({ userId, username }))
        .sort((a, b) => a.username.localeCompare(b.username));
      const regions = Array.from(regionSet).sort((a, b) => a.localeCompare(b));
      return { users, regions };
    } catch (error) {
      console.error('Error building community filter facets:', error);
      return { users: [], regions: [] };
    }
  }

  /**
   * Compute the dominant community-vote severity for each hazard id from
   * the user-store reaction cache. Ties are broken toward the higher severity. Hazards with no
   * severity votes map to null (treated as "unrated").
   */
  private async computeDominantSeverities(
    hazards: Hazard[]
  ): Promise<Map<string, SeverityLevel | null>> {
    const result = new Map<string, SeverityLevel | null>();
    if (hazards.length === 0) return result;
    const idSet = new Set(hazards.map(hazard => hazard.id));

    const localOnly = await reactionTable
      .filter(r => r.content_type === 'card' && !r.deleted && r.local_only === true && idSet.has(r.content_id))
      .toArray();

    const pending = new Map<string, Record<SeverityLevel, number>>();
    for (const reaction of localOnly) {
      const level = reaction.reaction_type_name as SeverityLevel;
      if (!SEVERITY_ORDER.includes(level)) continue;
      let counts = pending.get(reaction.content_id);
      if (!counts) {
        counts = { low: 0, medium: 0, high: 0, extreme: 0 };
        pending.set(reaction.content_id, counts);
      }
      counts[level] += 1;
    }

    for (const hazard of hazards) {
      const extra = pending.get(hazard.id);
      const published = hazard.reaction_counts;
      let best: SeverityLevel | null = null;
      let bestCount = 0;
      // Iterate ascending so ">= && >0" makes ties resolve to the higher level.
      for (const level of SEVERITY_ORDER) {
        const publishedCount = typeof published?.[level] === 'number' ? published[level] : 0;
        const count = publishedCount + (extra?.[level] ?? 0);
        if (count >= bestCount && count > 0) {
          best = level;
          bestCount = count;
        }
      }
      result.set(hazard.id, best);
    }
    return result;
  }

  /**
   * Filter hazards by any combination of criteria (all AND-combined) and
   * return a page. Online callers pass the in-memory metadata universe.
   * Offline callers omit it and the bounded window is filtered.
   */
  async getFilteredCommunityHazards(
    filters: CommunityFilters,
    page = 1,
    pageSize = 15,
    source?: Hazard[]
  ): Promise<{ hazards: Hazard[]; hasMore: boolean; totalCount: number }> {
    try {
      let all = source ?? await hazardGateway.list();

      // My hazards only
      if (filters.myHazardsOnly) {
        const currentUserId = await this.getCurrentUserId();
        all = currentUserId ? all.filter(h => h.user_id === currentUserId) : [];
      }

      // Authors
      if (filters.userIds.length > 0) {
        const set = new Set(filters.userIds);
        all = all.filter(h => !!h.user_id && set.has(h.user_id));
      }

      // Resolution status
      if (filters.status === 'resolved') all = all.filter(h => !!h.resolved);
      else if (filters.status === 'unresolved') all = all.filter(h => !h.resolved);

      // Region
      if (filters.regions.length > 0) {
        const set = new Set(filters.regions);
        all = all.filter(h => !!h.region && set.has(h.region.trim()));
      }

      // Date range (compare against the hazard's timestamp)
      if (filters.dateStart || filters.dateEnd) {
        const startMs = filters.dateStart
          ? moment(filters.dateStart, 'YYYY-MM-DD').startOf('day').valueOf()
          : -Infinity;
        const endMs = filters.dateEnd
          ? moment(filters.dateEnd, 'YYYY-MM-DD').endOf('day').valueOf()
          : Infinity;
        all = all.filter(h => {
          const t = safeDateParse(h.utc_timestamp);
          return t >= startMs && t <= endMs;
        });
      }

      // Search (single field)
      const term = filters.searchTerm.trim().toLowerCase();
      if (term) {
        if (filters.searchField === 'tags') {
          all = all.filter(h => (h.tags || []).some(t => t.toLowerCase().includes(term)));
        } else {
          all = all.filter(h => (h.description || '').toLowerCase().includes(term));
        }
      }

      // Severity from dominant community vote (do last; requires reaction lookup)
      if (filters.severities.length > 0) {
        const sevSet = new Set(filters.severities);
        const domMap = await this.computeDominantSeverities(all);
        all = all.filter(h => {
          const dom = domMap.get(h.id);
          return dom != null && sevSet.has(dom);
        });
      }

      // Newest first
      all.sort(compareHazardsNewestFirst);

      const totalCount = all.length;
      const startIndex = (page - 1) * pageSize;
      const paginated = all.slice(startIndex, startIndex + pageSize);
      return {
        hazards: paginated,
        hasMore: startIndex + paginated.length < totalCount,
        totalCount,
      };
    } catch (error) {
      console.error('Error getting filtered community hazards:', error);
      throw error;
    }
  }

  async getLocalHazardsWithPagination(page = 1, pageSize = 10): Promise<{ 
    hazards: Hazard[];
    hasMore: boolean;
    totalCount?: number;
  }> {
    try {
      // Get current user's non-deleted hazards from communityHazards
      const currentUserId = await this.getCurrentUserId();
      if (!currentUserId) {
        return {
          hazards: [],
          hasMore: false,
          totalCount: 0
        };
      }
      
      const allHazards = (await hazardGateway.list())
        .filter(hazard => hazard.user_id === currentUserId && !hazard.deleted);
      
      // Sort hazards by date (newest first)
      const sortedHazards = allHazards.sort(compareHazardsNewestFirst);
      
      // Calculate pagination
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedHazards = sortedHazards.slice(startIndex, endIndex);
      
      // Check if there are more hazards to load
      const hasMore = endIndex < sortedHazards.length;
      
      return {
        hazards: paginatedHazards,
        hasMore,
        totalCount: sortedHazards.length
      };
    } catch (error) {
      console.error('Error getting local hazards with pagination:', error);
      throw error;
    }
  }

  // Get comments for a hazard
  async getCommentsForHazard(hazardId: string): Promise<Comment[]> {
    try {
      // Get comments from local database that aren't deleted
      return await Promise.all(
        (await commentTable
          .where('incident_id')
          .equals(hazardId)
          .filter(comment => !comment.deleted && !comment.is_resolution)
          .sortBy('created_at')
        ).map(async (comment) => ({
          ...comment,
          image_data: await hazardGateway.resolveMediaRefs(comment.image_data ?? []),
        }))
      );
    } catch (error) {
      console.error(`Error getting comments for hazard ${hazardId}:`, error);
      throw error;
    }
  }

  // Get local cached comments for a hazard with pagination (plus any unsynced local_only comments)
  async getLocalCommentsForHazardPaginated(
    hazardId: string,
    page = 1,
    pageSize = 20
  ): Promise<{ comments: Comment[]; hasMore: boolean; totalCount: number }> {
    const allLocal = await this.getCommentsForHazard(hazardId);
    const start = Math.max(0, (page - 1) * pageSize);
    const pageComments = allLocal.slice(start, start + pageSize);
    const hasMore = start + pageComments.length < allLocal.length;

    // Always include any unsynced local_only optimistic comments if on page 1 or if they fall after the slice
    const pageIds = new Set(pageComments.map((c) => String(c.id)));
    const unsyncedOptimistic = allLocal.filter(
      (c) => c.local_only === true && !c.last_synced && c.id && !pageIds.has(String(c.id))
    );

    const combined = page === 1 && unsyncedOptimistic.length > 0
      ? [...pageComments, ...unsyncedOptimistic]
      : pageComments;

    return {
      comments: combined,
      hasMore,
      totalCount: allLocal.length,
    };
  }

  // Fetch a page of comments from the server (or local cache when offline) and store in Dexie
  async fetchCommentsPage(
    hazardId: string,
    pageSize = 20,
    offset = 0
  ): Promise<{ comments: Comment[]; hasMore: boolean }> {
    const pageNumber = Math.floor(offset / Math.max(1, pageSize)) + 1;
    if (!navigator.onLine) {
      const localResult = await this.getLocalCommentsForHazardPaginated(hazardId, pageNumber, pageSize);
      return {
        comments: localResult.comments,
        hasMore: localResult.hasMore,
      };
    }

    const params = new URLSearchParams({
      hazardId,
      limit: String(pageSize),
      offset: String(offset),
    });
    const fetchUrl = `${API_BASE_URL}/api/getCommentsForHazard?${params.toString()}`;
    const response = await fetchWithAuth(fetchUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch comments: ${response.statusText}`);
    }

    const rawServerComments: Comment[] = await response.json();
    const activeServerComments = rawServerComments.filter(
      (c) => !c.deleted && !c.is_resolution
    );

    const currentSyncTime = moment().format();
    const resolvedComments: Comment[] = [];
    const hydratedPage: Comment[] = [];

    for (const serverComment of activeServerComments) {
      const resolvedServerUsername = await userProfileService.resolveUsernameForUser(
        serverComment.user_id,
        { username: serverComment.username }
      );
      userProfileService.rememberUsername(
        serverComment.user_id,
        resolvedServerUsername || serverComment.username
      );

      const resolvedComment: Comment = {
        id: serverComment.id,
        incident_id: hazardId,
        user_id: serverComment.user_id,
        username: resolvedServerUsername || serverComment.username,
        profileImageUrl: serverComment.profileImageUrl,
        comment: serverComment.comment,
        created_at: serverComment.created_at,
        updated_at: serverComment.updated_at,
        deleted: serverComment.deleted ?? false,
        last_synced: currentSyncTime,
        local_only: false,
        image_data: serverComment.image_data || [],
        image_count: serverComment.image_count || (serverComment.image_data || []).length,
        media: serverComment.media,
        is_resolution: serverComment.is_resolution ?? false,
      };

      resolvedComments.push(resolvedComment);
      hydratedPage.push({
        ...resolvedComment,
        image_data: await hazardGateway.resolveMediaRefs(resolvedComment.image_data ?? []),
      });
    }

    if (resolvedComments.length > 0) {
      await hazardGateway.applyServerComments(resolvedComments);
    }

    // Preserve any unsynced optimistic local_only comments so newly posted offline/pending comments stay visible
    let hydratedUnsynced: Comment[] = [];
    if (offset === 0) {
      const localComments = await commentTable.where('incident_id').equals(hazardId).toArray();
      const pageIds = new Set(hydratedPage.map((c) => String(c.id)));
      const unsyncedLocal = localComments.filter(
        (c) =>
          !c.deleted &&
          !c.is_resolution &&
          c.local_only === true &&
          !c.last_synced &&
          c.id &&
          !pageIds.has(String(c.id))
      );

      hydratedUnsynced = await Promise.all(
        unsyncedLocal.map(async (c) => ({
          ...c,
          image_data: await hazardGateway.resolveMediaRefs(c.image_data ?? []),
        }))
      );
    }

    const combined =
      offset === 0 && hydratedUnsynced.length > 0
        ? [...hydratedPage, ...hydratedUnsynced].sort((a, b) =>
            String(a.created_at ?? '').localeCompare(String(b.created_at ?? ''))
          )
        : hydratedPage;

    const hasMore = rawServerComments.length === pageSize;

    // Also sync reactions for the synced comments in the background
    if (offset === 0 && activeServerComments.length > 0) {
      setTimeout(async () => {
        try {
          await reactionService.pushUnsyncedReactions();
        } catch (error) {
          console.warn('Failed to sync comment reactions after comment page fetch:', error);
        }
      }, 0);
    }

    return {
      comments: combined,
      hasMore,
    };
  }

  async getCommentsForHazardPaginated(
    hazardId: string,
    page = 1,
    pageSize = 20
  ): Promise<{ comments: Comment[]; hasMore: boolean }> {
    const offset = Math.max(0, (page - 1) * pageSize);
    return this.fetchCommentsPage(hazardId, pageSize, offset);
  }

  async getResolutionComment(hazardId: string): Promise<Comment | null> {
    const localResolution = await commentTable
      .where('incident_id')
      .equals(hazardId)
      .filter(comment => !comment.deleted && comment.is_resolution === true)
      .first();

    if (!navigator.onLine) {
      return localResolution ?? null;
    }

    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/getResolutionComment?hazardId=${encodeURIComponent(hazardId)}`);
      if (!response.ok) {
        return localResolution ?? null;
      }

      const serverComment = await response.json();
      if (serverComment === null || serverComment === undefined) {
        const localResolutionComments = await commentTable
          .where('incident_id')
          .equals(hazardId)
          .filter(comment => comment.is_resolution === true)
          .toArray();
        await Promise.all(localResolutionComments.map((comment) => commentTable.delete(comment.id!)));
        return null;
      }

      const currentSyncTime = moment().format();
      const merged: Comment = {
        id: serverComment.id,
        incident_id: hazardId,
        user_id: serverComment.user_id || '',
        username: serverComment.username,
        profileImageUrl: serverComment.profileImageUrl,
        comment: serverComment.comment ?? '',
        created_at: serverComment.created_at || currentSyncTime,
        updated_at: serverComment.updated_at || currentSyncTime,
        deleted: false,
        last_synced: currentSyncTime,
        local_only: false,
        image_data: serverComment.image_data || [],
        image_count: serverComment.image_count || (serverComment.image_data || []).length,
        is_resolution: true,
        can_edit_resolution: !!serverComment.can_edit_resolution
      };
      if (merged.id) {
        await commentTable.put(merged);
      } else {
        const localResolutionComments = await commentTable
          .where('incident_id')
          .equals(hazardId)
          .filter(comment => comment.is_resolution === true)
          .toArray();
        await Promise.all(localResolutionComments.map((comment) => commentTable.delete(comment.id!)));
      }
      return merged;
    } catch (error) {
      console.warn(`Failed to fetch resolution comment for hazard ${hazardId}:`, error);
      return localResolution ?? null;
    }
  }

  async upsertResolutionComment(
    hazardId: string,
    commentText: string,
    addedImages: File[] = [],
    deletedImageIndexes: number[] = []
  ): Promise<Comment> {
    if (!navigator.onLine) {
      throw new Error('Resolution messages can only be updated while online');
    }

    const uploadedMediaUrls = await mapWithConcurrency(addedImages, 3, async (file) => {
      const { blobUrl } = await uploadFileViaSas({ file });
      return blobUrl;
    });
    const payload = {
      hazard_id: hazardId,
      comment: commentText,
      added_images: normalizeMediaRefsForSync(uploadedMediaUrls),
      deleted_image_indexes: deletedImageIndexes
    };

    const response = await fetchWithAuth(`${API_BASE_URL}/api/upsertResolutionComment`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.error?.message || 'Failed to save resolution message');
    }

    const serverComment = await response.json();
    const currentSyncTime = moment().format();
    const merged: Comment = {
      id: serverComment.id,
      incident_id: hazardId,
      user_id: serverComment.user_id,
      username: serverComment.username,
      profileImageUrl: serverComment.profileImageUrl,
      comment: serverComment.comment || '',
      created_at: serverComment.created_at,
      updated_at: serverComment.updated_at,
      deleted: false,
      last_synced: currentSyncTime,
      local_only: false,
      image_data: serverComment.image_data || [],
      image_count: serverComment.image_count || (serverComment.image_data || []).length,
      is_resolution: true,
      can_edit_resolution: !!serverComment.can_edit_resolution
    };
    await commentTable.put(merged);
    return merged;
  }

  // Sync comments for a specific hazard
  async syncCommentsForHazard(hazardId: string): Promise<Comment[]> {
    try {
      if (!navigator.onLine) {
        return await this.getCommentsForHazard(hazardId);
      }
      
      // Get last sync timestamp for comments
      const lastSyncTimestamp = await readSyncMeta(`comments_sync_${hazardId}`);
      
      // Check if we have any unsynced comments (local_only = true) that might be missing from server
      // If so, do a full sync instead of incremental to ensure we don't miss anything
      const localComments = await commentTable.where('incident_id').equals(hazardId).toArray();
      const hasUnsyncedComments = localComments.some(c => c.local_only === true && !c.last_synced);
      
      let fetchUrl = `${API_BASE_URL}/api/getCommentsForHazard?hazardId=${hazardId}`;

      // Only use incremental sync if:
      // 1. We have a last sync timestamp
      // 2. We don't have any unsynced local comments (to avoid missing comments)
      if (lastSyncTimestamp && !hasUnsyncedComments) {
        // Convert ISO string to UNIX timestamp (seconds)
        const sinceTimestamp = Math.floor(new Date(lastSyncTimestamp).getTime() / 1000);
        fetchUrl = `${fetchUrl}&since=${sinceTimestamp}`;
        console.log(`[syncCommentsForHazard] Using incremental sync since ${sinceTimestamp} for hazard ${hazardId}`);
      } else {
        console.log(`[syncCommentsForHazard] Using full sync for hazard ${hazardId} (hasUnsynced: ${hasUnsyncedComments}, lastSync: ${lastSyncTimestamp})`);
      }

      let comments: Comment[] = [];

      // Fetch comments via HTTP
      const response = await fetchWithAuth(fetchUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch comments: ${response.statusText}`);
      }
      comments = await response.json();
      if (comments.length > 0) {
        await hazardGateway.applyServerComments(comments);
      }

      if (comments.length > 0) {
        const currentSyncTime = moment().format();
        // Store comments in Dexie, updating existing ones or adding new ones
        
        // First, get all local comments for this hazard to identify which ones are new
        const localComments = await commentTable.where('incident_id').equals(hazardId).toArray();
        const localCommentsByServerId = new Map<string, Comment>();
        
        localComments.forEach(comment => {
          if (comment.id) {
            localCommentsByServerId.set(String(comment.id), comment);
          }
        });
        
        // Process server comments - update existing ones and add new ones
        const serverCommentIdsProcessed = new Set<string>();
        
        for (const serverComment of comments) {
          // Skip deleted comments - they should already be filtered out by the server,
          // but this provides an extra safety layer
          if (serverComment.deleted) {
            console.log(`Skipping deleted comment ${serverComment.id} from server sync`);
            continue;
          }
          
          const serverCommentId = String(serverComment.id!);
          const resolvedServerUsername = await userProfileService.resolveUsernameForUser(serverComment.user_id, {
            username: serverComment.username
          });
          userProfileService.rememberUsername(serverComment.user_id, resolvedServerUsername || serverComment.username);
          serverCommentIdsProcessed.add(serverCommentId);
          
          const localComment = localCommentsByServerId.get(serverCommentId);
          
          if (localComment) {
            // Update local comment with server data
            await commentTable.update(localComment.id!, {
              comment: serverComment.comment,
              created_at: serverComment.created_at,
              updated_at: serverComment.updated_at,
              deleted: serverComment.deleted,
              last_synced: currentSyncTime,
              user_id: serverComment.user_id,
              username: resolvedServerUsername || serverComment.username,
              profileImageUrl: serverComment.profileImageUrl,
              local_only: false,
              image_data: serverComment.image_data || [],
              image_count: serverComment.image_count || 0,
              is_resolution: serverComment.is_resolution ?? false
            });
          } else {
            // Add new comment from server
            try {
              await commentTable.add({
                id: serverComment.id,
                incident_id: hazardId,
                user_id: serverComment.user_id,
                username: resolvedServerUsername || serverComment.username,
                profileImageUrl: serverComment.profileImageUrl,
                comment: serverComment.comment,
                created_at: serverComment.created_at,
                updated_at: serverComment.updated_at,
                deleted: serverComment.deleted,
                last_synced: currentSyncTime,
                local_only: false,
                image_data: serverComment.image_data || [],
                image_count: serverComment.image_count || 0,
                is_resolution: serverComment.is_resolution ?? false
              });
            } catch (error: any) {
              // If add fails (e.g., already exists), try update instead
              if (error.name === 'ConstraintError') {
                console.log(`Comment ${serverComment.id} already exists, updating instead`);
                await commentTable.update(serverComment.id!, {
                  comment: serverComment.comment,
                  created_at: serverComment.created_at,
                  updated_at: serverComment.updated_at,
                  deleted: serverComment.deleted,
                  last_synced: currentSyncTime,
                  user_id: serverComment.user_id,
                  username: resolvedServerUsername || serverComment.username,
                  profileImageUrl: serverComment.profileImageUrl,
                  local_only: false,
                  image_data: serverComment.image_data || [],
                  image_count: serverComment.image_count || 0,
                  is_resolution: serverComment.is_resolution ?? false
                });
              } else {
                throw error;
              }
            }
          }
        }
        
        // Preserve comments that were received via WebSocket but aren't in server response
        // This can happen if sync uses incremental `since` timestamp that excludes them
        for (const localComment of localComments) {
          const localCommentId = String(localComment.id!);
          if (!serverCommentIdsProcessed.has(localCommentId) && 
              !localComment.deleted && 
              !localComment.local_only) {
            // This is a WebSocket comment that wasn't in server response
            // Preserve it but mark as needing verification on next sync
            console.log(`[syncCommentsForHazard] Preserving WebSocket comment ${localComment.id} not in server response`);
            // Don't update last_synced to force it to be included in next sync
          }
        }

        // Clean up any deleted comments that might exist in local database
        // BUT preserve comments that were received via WebSocket and haven't been synced yet
        // Only delete comments that are explicitly marked as deleted AND have been synced
        const allLocalComments = await commentTable.where('incident_id').equals(hazardId).toArray();
        const serverCommentIds = new Set(comments.map(c => String(c.id)));
        
        for (const localComment of allLocalComments) {
          // Only delete if:
          // 1. Comment is marked as deleted
          // 2. Comment exists on server (was synced) OR comment is local_only and older than 5 minutes
          if (localComment.deleted === true) {
            const isOnServer = serverCommentIds.has(String(localComment.id));
            const isOldLocalOnly = localComment.local_only && localComment.created_at && 
              (Date.now() - new Date(localComment.created_at).getTime()) > 5 * 60 * 1000;
            
            if (isOnServer || isOldLocalOnly) {
              await commentTable.delete(localComment.id!);
              console.log(`Cleaned up deleted comment ${localComment.id} from IndexedDB`);
            }
          }
        }

        // Update last sync timestamp
        await writeSyncMeta(`comments_sync_${hazardId}`, currentSyncTime);
        
        // Emit an event that comments have been synced
        eventEmitter.emit(EVENTS.COMMENTS_SYNCED, hazardId);
        
        // Also sync reactions for the synced comments in the background
        setTimeout(async () => {
          try {
            await reactionService.pushUnsyncedReactions();
          } catch (error) {
            console.warn('Failed to sync comment reactions after comment sync:', error);
          }
        }, 0);
      }

      // Return the updated list of comments
      return await this.getCommentsForHazard(hazardId);
    } catch (error) {
      console.error(`Error syncing comments for hazard ${hazardId}:`, error);
      throw error;
    }
  }
  
  // Push unsynced comments to the server
  async pushUnsyncedComments(): Promise<void> {
    if (!navigator.onLine) {
      console.log('📴 Cannot sync comments - offline');
      return;
    }

    // Fetch all comments and filter for those with undefined last_synced value
    const allUnsyncedComments = await commentTable
      .filter(comment => comment.local_only === true && comment.last_synced === undefined)
      .toArray();

    console.log(`💬 Found ${allUnsyncedComments.length} unsynced comments to push`);

    if (allUnsyncedComments.length === 0) {
      return;
    }

    // Log details about unsynced comments for debugging
    allUnsyncedComments.forEach(comment => {
      console.log(`📝 Unsynced comment: ${comment.id}, deleted: ${comment.deleted}, local_only: ${comment.local_only}, incident: ${comment.incident_id}`);
    });

    // Attempt to push each unsynced comment sequentially
    for (const comment of allUnsyncedComments) {
      const queued = await hazardGateway.findOpByEntity(comment.id!);
      if (queued) {
        continue;
      }
      const commentMeta = comment as Comment & {
        sync_action?: 'create' | 'update';
        pending_added_images?: string[];
        pending_deleted_image_indexes?: number[];
      };
      const success = commentMeta.sync_action === 'update' && !comment.deleted
        ? await this.syncUpdatedCommentAsync(
            comment,
            commentMeta.pending_added_images || [],
            commentMeta.pending_deleted_image_indexes || []
          )
        : await pushSingleCommentSubmission(comment);
      console.log(`💬 Comment ${comment.id} sync result: ${success ? '✅ success' : '❌ failed'}`);
    }
  }

  // Add this near the pushUnsyncedComments method
  async pushUnsyncedReactions(): Promise<void> {
    try {
      if (!navigator.onLine) {
        console.log('Offline, skipping push of unsynced reactions.');
        return;
      }

      // Let reactionService handle this
      await reactionService.pushUnsyncedReactions();
    } catch (error) {
      console.error('Error pushing unsynced reactions:', error);
    }
  }

  // Update the syncHazards or other sync methods to include this
  async syncAll(): Promise<void> {
    try {
      await this.syncHazards();
      await this.pushUnsyncedHazards();
      await this.pushUnsyncedComments();
      await this.pushUnsyncedReactions();
    } catch (error) {
      console.error('Error in syncAll:', error);
    }
  }

  // Get offline queue status for debugging and user feedback
  async getOfflineQueueStatus(): Promise<{
    totalUnsynced: number;
    newHazards: number;
    updatedHazards: number;
    deletedHazards: number;
    errorHazards: number;
    details: Array<{
      id: string;
      type: 'new' | 'updated' | 'deleted' | 'error';
      description: string;
      timestamp: string;
      error?: string;
    }>;
  }> {
    try {
      // Get hazards for current user
      const currentUserId = await this.getCurrentUserId();
      if (!currentUserId) {
        return {
          totalUnsynced: 0,
          newHazards: 0,
          updatedHazards: 0,
          deletedHazards: 0,
          errorHazards: 0,
          details: []
        };
      }
      
      const pending = await hazardGateway.listPendingHazards();
      const trulyUnsynced = pending.map(({ kind, hazard }) => {
        if (kind === 'hazard.delete') {
          return { ...hazard, deleted: true };
        }
        if (kind === 'hazard.update' || kind === 'hazard.resolve') {
          return { ...hazard, last_synced: hazard.last_synced ?? new Date(0).toISOString() };
        }
        return hazard;
      });

      const status = {
        totalUnsynced: trulyUnsynced.length,
        newHazards: 0,
        updatedHazards: 0,
        deletedHazards: 0,
        errorHazards: 0,
        details: [] as Array<{
          id: string;
          type: 'new' | 'updated' | 'deleted' | 'error';
          description: string;
          timestamp: string;
          error?: string;
        }>
      };

      for (const hazard of trulyUnsynced) {
        let type: 'new' | 'updated' | 'deleted' | 'error';
        let description = hazard.description || 'No description';
        
        if (hazard.deleted) {
          type = 'deleted';
          status.deletedHazards++;
        } else if (hazard.sync_error) {
          type = 'error';
          status.errorHazards++;
        } else {
          // Check if this hazard exists in communityHazards (indicating it was previously synced)
          const existingCommunityHazard = await hazardGateway.get(hazard.id);
          if (existingCommunityHazard) {
            type = 'updated';
            status.updatedHazards++;
          } else {
            type = 'new';
            status.newHazards++;
          }
        }

        status.details.push({
          id: hazard.id,
          type,
          description: description.length > 50 ? description.substring(0, 50) + '...' : description,
          timestamp: hazard.utc_timestamp,
          error: hazard.sync_error
        });
      }

      return status;
    } catch (error) {
      console.error('Error getting offline queue status:', error);
      return {
        totalUnsynced: 0,
        newHazards: 0,
        updatedHazards: 0,
        deletedHazards: 0,
        errorHazards: 0,
        details: []
      };
    }
  }

  // Fast metadata-only method for initial community hazards loading.
  // When a search term is present this is the SEARCH flow: results are returned
  // to the caller only and are NOT written to the communityHazards cache (the
  // feed metadata path below keeps its cache writes). Reaction ingestion still
  // runs so offline severity filtering stays accurate.
  async getHazardsMetadataFast(
    page = 1,
    pageSize = 10,
    search?: { searchTerm?: string; searchField?: SearchField },
    options?: { bypassCache?: boolean; offset?: number }
  ): Promise<{
    hazards: Hazard[];
    hasMore: boolean;
    totalCount?: number;
  }> {
    try {
      const offset = options?.offset ?? (page - 1) * pageSize;
      const metadataPage = await this.fetchIncidentsMetadataPage(
        pageSize,
        offset,
        search,
        options?.bypassCache ? { bypassCache: true } : undefined
      );
      const hazards = metadataPage.hazards;

      if (!metadataPage.notModified && hazards.length > 0) {
        await processIncidentReactions(hazards);
      }

      const activeHazardsForResponse = hazards.filter(hazard => !hazard.deleted);
      const hasMore = hazards.length === pageSize;

      console.log(`getHazardsMetadataFast: page=${page}, pageSize=${pageSize}, got ${hazards.length} hazards (${activeHazardsForResponse.length} active), hasMore=${hasMore}`);

      return {
        hazards: activeHazardsForResponse,
        hasMore,
        totalCount: hazards.length
      };
    } catch (error) {
      console.error('Error getting fast hazards metadata:', error);
      throw error;
    }
  }


}

const hazardService = new HazardService();
export default hazardService;